import React, { useState } from 'react';
import {
  Button,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  offers,
  RoundStatus,
  listings,
  OfferStatus as OS,
} from '@prisma/client';
import { capitalize } from 'lodash';
import NiceModal from '@ebay/nice-modal-react';
import OfferConfirmModal, {
  OFFER_CONFIRM_MODAL_TYPE,
} from '../OfferConfirmationModal/OfferConfirmModal';
import { NegotiationForm } from '../NegotiationForm/NegotiationForm';
import offerRoundsService from '../../api/services/offerRounds';
import offersService from '../../api/services/offers';
import { useAuth } from '../../contexts/AuthContext';

interface OfferActionsProps {
  offer: offers & { currentRound?: any; offerRounds?: any[] };
  onOfferAccepted: (offer: offers) => void;
  onActionComplete: () => void;
  token?: string;
  isPublicView?: boolean;
  isBuyer?: boolean;
  isAdmin?: boolean;
  listing?: listings;
}

export const OfferActions: React.FC<OfferActionsProps> = ({
  offer,
  onOfferAccepted,
  onActionComplete,
  token,
  isPublicView = false,
  isBuyer = false,
  isAdmin = false,
  listing,
}) => {
  const { currentUser } = useAuth();
  const [negotiationOpen, setNegotiationOpen] = useState(false);
  const [negotiationMode, setNegotiationMode] = useState<'counter'>('counter');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [buyerConfirmOpen, setBuyerConfirmOpen] = useState(false);
  const [confirmDialogType, setConfirmDialogType] = useState<
    'accept' | 'decline'
  >('accept');
  const { status } = offer;

  // Check if the current user has an active counter offer waiting for response
  // Admins can always act regardless of who initiated the counter
  const hasActiveCounter = (): boolean => {
    if (!offer.currentRound || isPublicView || isAdmin) return false;

    const isPending =
      offer.currentRound.status === RoundStatus.PENDING ||
      offer.currentRound.status === RoundStatus.COUNTERED;

    const isMyCounter = offer.currentRound.initiatedBy === currentUser?.id;

    return isPending && isMyCounter;
  };

  // Handle REVIEWED status
  if (status === OS.REVIEWED) {
    return (
      <Typography color="success.main" fontWeight={600}>
        Ready for Review
      </Typography>
    );
  }

  // Handle SIGNED status - show actions for public view (seller's agent), just status for others
  if (status === OS.SIGNED && !isPublicView) {
    return (
      <Typography color="success.main" fontWeight={600}>
        Offer Signed
      </Typography>
    );
  }

  // Handle other non-actionable statuses
  if (status !== 'PENDING' && status !== 'COUNTERED' && status !== 'SIGNED') {
    return (
      <Typography color={status === 'ACCEPTED' ? 'success.main' : 'error.main'}>
        {capitalize(status)}
      </Typography>
    );
  }

  const handleNegotiationSubmit = async (data: any) => {
    try {
      // Create a new counter offer round
      await offerRoundsService.create(
        {
          ...data,
          offerId: offer.id,
        },
        token
      );
      onActionComplete();
    } catch (error) {
      console.error('Error handling negotiation:', error);
    }
  };

  const openNegotiation = () => {
    setNegotiationOpen(true);
  };

  const handlePublicViewAccept = async () => {
    try {
      await offersService.patch(offer.id, { status: 'ACCEPTED' }, token);
      onOfferAccepted(offer);
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Error accepting offer:', error);
    }
  };

  const handlePublicViewDecline = async () => {
    try {
      await offersService.patch(offer.id, { status: 'DECLINED' }, token);
      onActionComplete();
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Error declining offer:', error);
    }
  };

  const handleBuyerAccept = async () => {
    try {
      // If there's a current round (counter offer), accept it via the round service
      if (status === 'COUNTERED' && offer.currentRound) {
        await offerRoundsService.accept(offer.currentRound.id, token);
      } else {
        await offersService.patch(offer.id, { status: 'ACCEPTED' }, token);
      }
      onOfferAccepted(offer);
      setBuyerConfirmOpen(false);
    } catch (error) {
      console.error('Error accepting offer:', error);
    }
  };

  // If current user has an active counter, show waiting message
  if (hasActiveCounter()) {
    return null;
  }

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Button
          color="success"
          variant="contained"
          onClick={() => {
            if (isPublicView) {
              // For public view, use simple confirmation dialog
              setConfirmDialogType('accept');
              setConfirmDialogOpen(true);
            } else if (isBuyer || isAdmin) {
              // For buyers and admins, use simple confirmation
              setBuyerConfirmOpen(true);
            } else {
              // For sellers, use the full modal with disclosures
              NiceModal.show(OfferConfirmModal, {
                handleOnSubmit: async (acceptedOffer: offers) => {
                  // If there's a current round (counter offer), accept it via the round service
                  if (status === 'COUNTERED' && offer.currentRound) {
                    try {
                      await offerRoundsService.accept(
                        offer.currentRound.id,
                        token
                      );
                    } catch (error) {
                      console.error('Error accepting counter offer:', error);
                    }
                  }
                  onOfferAccepted(acceptedOffer);
                },
                offer,
                type: OFFER_CONFIRM_MODAL_TYPE.ACCEPT,
              });
            }
          }}
        >
          Accept
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => openNegotiation()}
        >
          {status === 'COUNTERED' ? 'Counter Back' : 'Counter'}
        </Button>
        <Button
          color="error"
          variant="outlined"
          onClick={() => {
            if (isPublicView) {
              // For public view, use simple confirmation dialog
              setConfirmDialogType('decline');
              setConfirmDialogOpen(true);
            } else {
              // For all authenticated users (buyers and sellers), use the confirmation modal
              NiceModal.show(OfferConfirmModal, {
                handleOnSubmit: async () => {
                  // If there's a current round (counter offer), reject it via the round service
                  if (status === 'COUNTERED' && offer.currentRound) {
                    try {
                      await offerRoundsService.reject(
                        offer.currentRound.id,
                        token
                      );
                    } catch (error) {
                      console.error('Error rejecting counter offer:', error);
                    }
                  }
                  onActionComplete();
                },
                offer,
                type: OFFER_CONFIRM_MODAL_TYPE.DECLINE,
              });
            }
          }}
        >
          {status === 'COUNTERED' ? 'Reject' : 'Decline'}
        </Button>
      </Stack>

      <NegotiationForm
        open={negotiationOpen}
        onClose={() => setNegotiationOpen(false)}
        offer={offer}
        lastRound={offer.currentRound}
        onSubmit={handleNegotiationSubmit}
        mode="counter"
        token={token}
        isBuyer={isBuyer}
        isPublicView={isPublicView}
        listing={listing}
      />

      {/* Simple confirmation dialog for public view */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialogType === 'accept' ? 'Accept Offer' : 'Decline Offer'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialogType === 'accept'
              ? 'Are you sure you want to accept this offer on behalf of your client?'
              : 'Are you sure you want to decline this offer on behalf of your client?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={
              confirmDialogType === 'accept'
                ? handlePublicViewAccept
                : handlePublicViewDecline
            }
            color={confirmDialogType === 'accept' ? 'success' : 'error'}
            variant="contained"
          >
            {confirmDialogType === 'accept' ? 'Accept' : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Simple confirmation dialog for buyers/admins */}
      <Dialog
        open={buyerConfirmOpen}
        onClose={() => setBuyerConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Accept Offer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to accept this offer?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuyerConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleBuyerAccept}
            color="success"
            variant="contained"
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
